import * as React from 'react'
import { json, type DataFunctionArgs, type MetaFunction } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import invariant from 'tiny-invariant'
import Lightbox from 'yet-another-react-lightbox'

import { type Album } from '~/types.ts'
import { cms } from '~/services/cms.server.ts'
import { getMetas, getUrl } from '~/utils/index.ts'
import { Button } from '~/components/Button.tsx'
import { Image } from '~/components/Image.tsx'
import { Hero } from '~/components/sections/Hero.tsx'
import { Typography } from '~/components/Typography.tsx'
import { getGenericSocialImage } from '~/images.ts'
import { type LoaderData as RootLoaderData } from '~/root.tsx'

export const handle: SEOHandle = {
	getSitemapEntries: async request => {
		const {
			portfolioCollection: { items: albums },
		} = await cms<{
			portfolioCollection: {
				items: Pick<Album, 'slug'>[]
			}
		}>(`
		{
			portfolioCollection(limit: 20) {
				items {
					slug,
					heroImage {
						url
					}
					videosCollection {
						total
					}
					photosCollection {
						total
					}
				}
			}
		}`)
		return albums.map(album => {
			return {
				route: `/portfolio/${album.slug}`,
				priority: 0.5,
			}
		})
	},
}

export async function loader({ params }: DataFunctionArgs) {
	const { slug } = params
	invariant(typeof slug === 'string', 'Missing slag')
	const {
		portfolioCollection: { items: album },
	} = await cms<{ portfolioCollection: { items: Album[] } }>(`
  {
    portfolioCollection(where: {
      slug: "${slug}"
    }, limit: 1) {
      items {
        title
        heroImage {
          url
        }
        photosCollection {
          items {
            url
            width
            height
          }
        }
        videosCollection {
          items {
            url
            thumbnail {
              url
              width
              height
            }
          }
        }
      }
    }
  }`)

	invariant(album[0] !== undefined, "Album with that slug doesn't exist")

	return json({ album: album[0] })
}

export const meta: MetaFunction<typeof loader, { root: RootLoaderData }> = ({
	matches,
	data,
}) => {
	const requestInfo = matches.find(m => m.id === 'root')?.data.requestInfo
	return getMetas({
		url: getUrl(requestInfo),
		origin: requestInfo?.origin ?? '',
		title: data?.album.title,
		album_name: data?.album.title,
		image: getGenericSocialImage({
			words: data?.album.title || '',
			featuredImage: data?.album.heroImage.url || '',
		}),
	})
}

function CustomImage({
	src,
	width,
	height,
	handleClick,
}: {
	src: string
	width: number
	height: number
	handleClick: () => void
}) {
	const newWidth = Math.floor(width / 3)
	const newHeight = Math.floor(height / 3)

	return (
		<button className="cursor-pointer overflow-hidden" onClick={handleClick}>
			<Image
				webp={`${src}?w=${newWidth}&h=${newHeight}&fm=webp`}
				png={`${src}?w=${newWidth}&h=${newHeight}&fm=png`}
				className="transition duration-500 hover:scale-110"
				loading="lazy"
				width={newWidth}
				height={newHeight}
			/>
		</button>
	)
}

export default function AlbumRoute() {
	const { album } = useLoaderData<typeof loader>()
	const images = album.photosCollection.items.map(el => ({
		src: el.url,
		width: el.width,
		height: el.height,
	}))

	const [index, setIndex] = React.useState(-1)
	const contentRef = React.useRef<HTMLDivElement>(null)
	const handleClick = (index: number) => setIndex(index)

	return (
		<div>
			<Hero
				content={
					<div className="relative grid h-full grid-rows-2 ">
						<div className="self-center">
							<Typography color="key" as="h1">
								Album
							</Typography>
							<Typography as="h1">{album.title}</Typography>
						</div>

						<button
							onClick={() => contentRef.current?.scrollIntoView()}
							className="scroll self-end justify-self-center"
						>
							<span></span>
							<span></span>
							<span></span>Scroll
						</button>
					</div>
				}
				img={{
					webp: `${album.heroImage.url}?h=1080&fm=webp`,
					png: `${album.heroImage.url}?h=1080&fm=png`,
					webpSm: `${album.heroImage.url}?h=640&fm=webp`,
					pngSm: `${album.heroImage.url}?h=640&fm=png`,
				}}
			/>
			<div
				ref={contentRef}
				className="my-16 flex flex-col gap-16 px-6 md:px-12"
			>
				{album.videosCollection.items.length > 0 ? (
					<section id="video">
						<Typography as="h2">Wideo</Typography>
						<div className="flex flex-wrap gap-2">
							{album.videosCollection.items.map(video => (
								<a
									key={video.url}
									target="_blank"
									href={video.url}
									className="group relative flex aspect-video w-48 flex-col items-center justify-center overflow-hidden bg-black md:w-80"
									rel="noreferrer"
								>
									<Image
										webp={`${video.thumbnail.url}?h=256&fm=webp`}
										png={`${video.thumbnail.url}?h=256&fm=png`}
										className="w-48 flex-1 object-cover object-center transition-all duration-500 group-hover:scale-110 group-hover:blur-sm md:w-80"
									/>
									<svg
										width="28"
										height="28"
										viewBox="0 0 28 28"
										xmlns="http://www.w3.org/2000/svg"
										className="absolute h-9 w-9 fill-white shadow-lg transition duration-300 group-hover:scale-110"
									>
										<path d="M26.1601 12.0801L2.94007 0.470113C2.62682 0.324981 2.28224 0.260501 1.93771 0.282544C1.59318 0.304587 1.25963 0.412452 0.967429 0.59632C0.675228 0.780188 0.433654 1.03422 0.2647 1.33529C0.0957449 1.63636 0.00477479 1.97491 7.02113e-05 2.32011V25.5301C-0.00285516 25.8845 0.08568 26.2336 0.257119 26.5437C0.428558 26.8539 0.677102 27.1145 0.978729 27.3005C1.28036 27.4865 1.62487 27.5916 1.97896 27.6055C2.33305 27.6195 2.68475 27.5418 3.00007 27.3801L26.1601 15.7701C26.5031 15.5979 26.7916 15.3337 26.9931 15.007C27.1947 14.6803 27.3014 14.304 27.3014 13.9201C27.3014 13.5362 27.1947 13.1599 26.9931 12.8332C26.7916 12.5065 26.5031 12.2423 26.1601 12.0701V12.0801Z" />
									</svg>
								</a>
							))}
						</div>
					</section>
				) : null}

				<section id="photo">
					<Typography as="h2">Zdjęcia</Typography>
					<ResponsiveMasonry
						columnsCountBreakPoints={{
							350: 1,
							750: 2,
							900: 3,
							1400: 4,
							2000: 5,
						}}
					>
						{/* @ts-ignore */}
						<Masonry gutter="1rem">
							{images.map((img, i) => (
								<CustomImage
									key={img.src}
									src={img.src}
									width={img.width}
									height={img.height}
									handleClick={() => handleClick(i)}
								/>
							))}
						</Masonry>
					</ResponsiveMasonry>

					<Lightbox
						slides={images}
						open={index >= 0}
						index={index}
						close={() => setIndex(-1)}
					/>
				</section>
				<Link className="mx-auto mt-8 inline-block" to="/#portfolio">
					<Button size="large">Zobacz inne</Button>
				</Link>
			</div>
		</div>
	)
}
